import { useState } from 'react';
import { Button } from '@/components/ui';
import { backend } from '@/api/backend';
import { explainLine } from '@/api/explain';
import { newIdempotencyKey } from '@/api/http';

/**
 * The permission a bucket needs before it can be walked at all.
 *
 * A walk reads file contents, which needs a role in the account that owns the
 * bucket. Registering that role only existed as SQL, so a bucket without one
 * simply failed to walk and nothing on screen said why or what to do. This is
 * the precondition of the whole workflow, not a setting, so it lives where an
 * operator hits it.
 *
 * The ARN is validated to AWS's own shape before it is sent, because a rejected
 * write here reads as "the tool is broken" rather than "that was a typo".
 */

/** AWS role ARNs, and nothing else. */
export function roleArnProblem(value: string): string | null {
  if (!value.trim()) return null;
  if (!/^arn:aws[a-z-]*:iam::\d{12}:role\/.+/.test(value.trim())) {
    return 'That is not an IAM role ARN. It looks like arn:aws:iam::123456789012:role/YourRole.';
  }
  return null;
}

export function ContentReaderRole({ bucketId, onRegistered }: { bucketId: string; onRegistered?: () => void }) {
  const [arn, setArn] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const problem = roleArnProblem(arn);

  const submit = async () => {
    setBusy(true);
    setNote(null);
    try {
      const r = (await backend.registerContentReaderRole(bucketId, arn.trim(), newIdempotencyKey())) as {
        data?: { created?: boolean };
        created?: boolean;
      };
      const created = r.data?.created ?? r.created;
      setNote(
        created === false
          ? 'That role was already registered for this bucket, so nothing changed.'
          // What happened is that a string was stored. Nothing proved the
          // role exists, is assumable by the walk executor, or carries read
          // permission, and this is the one component whose whole job is the
          // difference between a registered bucket and a walkable one.
          : 'Role recorded for this bucket. The next walk will use it.',
      );
      onRegistered?.();
    } catch (e) {
      setNote(explainLine(e, 'register read access'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 px-4 py-3">
      <input
        value={arn}
        onChange={(e) => setArn(e.target.value)}
        placeholder="arn:aws:iam::123456789012:role/PoseidonContentReader"
        aria-label="Content reader role ARN"
        className="mono min-w-0 flex-1 rounded border border-line bg-panel-2 px-2.5 py-1.5 text-small text-ink placeholder:text-ink-4 focus:border-acc focus:outline-none"
      />
      <Button
        disabled={busy || !arn.trim() || Boolean(problem)}
        loading={busy}
        onClick={() => void submit()}
      >
        {busy ? 'Registering…' : 'Register the role'}
      </Button>
      <span className="w-full text-caption text-ink-4">
        {problem ??
          note ??
          'A walk reads file contents, which needs a role in the account that owns this bucket. '}
      </span>
    </div>
  );
}
